import { AnalysisSummary as AnalysisItem } from '../../../services/api'

export type View = 'attention' | 'informational' | 'reviewed' | 'all'
export type SortBy = 'urgency' | 'newest' | 'oldest' | 'deadline'
export type DateRange = 'all' | '30' | '60' | '90' | '120'

export interface Filters {
  q: string
  crit: string
  view: View
  country: string
  modules: string[]
  showNotRelevant: boolean
  dateRange: DateRange
  sortBy: SortBy
  hidePastDeadline: boolean
}

export const DEFAULT_FILTERS: Filters = {
  q: '', crit: 'all', view: 'attention', country: 'all', modules: [], showNotRelevant: false,
  dateRange: 'all', sortBy: 'urgency', hidePastDeadline: true,
}

export const DATE_RANGE_OPTIONS: { id: DateRange; label: string }[] = [
  { id: 'all', label: 'All time' },
  { id: '30', label: 'Last 30 days' },
  { id: '60', label: 'Last 60 days' },
  { id: '90', label: 'Last 90 days' },
  { id: '120', label: 'Last 120 days' },
]

export const SORT_OPTIONS: { id: SortBy; label: string }[] = [
  { id: 'urgency', label: 'Urgency' },
  { id: 'newest', label: 'Newest first' },
  { id: 'oldest', label: 'Oldest first' },
  { id: 'deadline', label: 'Deadline (soonest)' },
]

export function withinDateRange(analysedAt: string | null, range: DateRange): boolean {
  if (range === 'all') return true
  if (!analysedAt) return false
  const days = parseInt(range, 10)
  const cutoff = Date.now() - days * 86400000
  return new Date(analysedAt).getTime() >= cutoff
}

export function sortItems(a: AnalysisItem, b: AnalysisItem, sortBy: SortBy): number {
  switch (sortBy) {
    case 'urgency':
      return urgencyScore(a) - urgencyScore(b)
    case 'newest':
      return (new Date(b.analysed_at ?? 0).getTime()) - (new Date(a.analysed_at ?? 0).getTime())
    case 'oldest':
      return (new Date(a.analysed_at ?? 0).getTime()) - (new Date(b.analysed_at ?? 0).getTime())
    case 'deadline':
      return daysUntil(a.compliance_deadline) - daysUntil(b.compliance_deadline)
  }
}

export function daysUntil(d: string | null): number {
  if (!d) return 9999
  return Math.ceil((new Date(d).getTime() - Date.now()) / 86400000)
}

export function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function timeAgo(iso: string | null): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(ms / 60000)
  if (mins < 60) return 'just now'
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  return `${Math.floor(days / 30)}mo ago`
}

export function isRecent(iso: string | null): boolean {
  if (!iso) return false
  return Date.now() - new Date(iso).getTime() < 48 * 3600000
}

export function urgencyScore(a: AnalysisItem): number {
  const critOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }
  return (
    (critOrder[a.criticality] ?? 4) * 10000 +
    (a.review_status === 'pending' ? 0 : 5000) +
    (a.requires_action ? 0 : 2500) +
    Math.min(Math.max(daysUntil(a.compliance_deadline), 0), 2499)
  )
}

const RESOLVED = new Set(['accepted', 'rejected', 'dismissed'])

export function matchesView(i: AnalysisItem, view: View): boolean {
  if (view === 'all') return true
  if (i.is_relevant === false) return false
  if (RESOLVED.has(i.review_status)) return view === 'reviewed'
  return view === (i.requires_action ? 'attention' : 'informational')
}

export const CRIT: Record<string, { label: string; c: string; bg: string }> = {
  critical: { label: 'Critical', c: 'var(--an-red)', bg: 'var(--an-red-bg)' },
  high: { label: 'High', c: 'var(--an-amber)', bg: 'var(--an-amber-bg)' },
  medium: { label: 'Medium', c: 'var(--an-yellow)', bg: 'var(--an-yellow-bg)' },
  low: { label: 'Low', c: 'var(--an-green)', bg: 'var(--an-green-bg)' },
  // Without this key every informational row (93% of all analyses) fell
  // through to the `?? CRIT.medium` fallback and rendered as "Medium".
  informational: { label: 'Informational', c: 'var(--an-muted)', bg: 'var(--an-muted-bg)' },
}

export const STATUS: Record<string, { label: string; c: string; bg: string }> = {
  pending: { label: 'Pending', c: 'var(--an-yellow)', bg: 'var(--an-yellow-bg)' },
  accepted: { label: 'Accepted', c: 'var(--an-green)', bg: 'var(--an-green-bg)' },
  rejected: { label: 'Rejected', c: 'var(--an-red)', bg: 'var(--an-red-bg)' },
  dismissed: { label: 'Dismissed', c: 'var(--an-muted)', bg: 'var(--an-muted-bg)' },
  informational: { label: 'Informational', c: 'var(--an-blue)', bg: 'var(--an-blue-bg)' },
  not_relevant: { label: 'Not Relevant', c: 'var(--an-muted)', bg: 'var(--an-muted-bg)' },
}

export const IMPACT: Record<string, { label: string; c: string; bg: string }> = {
  direct: { label: 'Direct', c: 'var(--an-red)', bg: 'var(--an-red-bg)' },
  indirect: { label: 'Indirect', c: 'var(--an-amber)', bg: 'var(--an-amber-bg)' },
  potential: { label: 'Potential', c: 'var(--an-muted)', bg: 'var(--an-muted-bg)' },
}

export const OWNER: Record<string, string> = {
  workday_vendor: 'Workday',
  kainos: 'Kainos',
  client: 'Client',
  multiple: 'Multiple Parties',
  none: 'None',
}

export const TAG_LABEL: Record<string, string> = {
  workday_hcm: 'Workday HCM',
  workday_payroll: 'Workday Payroll',
  workday_finance: 'Workday Finance',
  workday_platform: 'Workday Platform',
  data_privacy: 'Data Privacy',
  general_compliance: 'General Compliance',
}

// Every EU member state plus the UK, so a new source needs no frontend change.
const COUNTRY_NAMES: Record<string, string> = {
  EU: 'European Union',
  AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria', CY: 'Cyprus', CZ: 'Czechia',
  DE: 'Germany', DK: 'Denmark', EE: 'Estonia', ES: 'Spain', FI: 'Finland',
  FR: 'France', GR: 'Greece', HR: 'Croatia', HU: 'Hungary', IE: 'Ireland',
  IT: 'Italy', LT: 'Lithuania', LU: 'Luxembourg', LV: 'Latvia', MT: 'Malta',
  NL: 'Netherlands', PL: 'Poland', PT: 'Portugal', RO: 'Romania', SE: 'Sweden',
  SI: 'Slovenia', SK: 'Slovakia',
  GB: 'United Kingdom',
}

// A flag emoji is the two-letter code spelled in regional-indicator letters.
function flagFor(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return ''
  return String.fromCodePoint(...Array.from(code, ch => 0x1f1e6 + ch.charCodeAt(0) - 65))
}

export function countryMeta(code: string | null) {
  const c = (code ?? 'EU').toUpperCase()
  return { label: COUNTRY_NAMES[c] ?? c, flag: flagFor(c) }
}

export const VIEWS: { id: View; label: string }[] = [
  { id: 'attention', label: 'Needs action' },
  { id: 'informational', label: 'Informational' },
  { id: 'reviewed', label: 'Reviewed' },
  { id: 'all', label: 'All' },
]
